import { useState, useRef, useEffect } from 'react';
import { apiClient } from '@/api/client';
import toast from 'react-hot-toast';

const TTSTestSection = () => {
  const [text, setText] = useState('Hello, welcome to ameboGPT! Experience authentic Nigerian voices.');
  const [language, setLanguage] = useState<'english' | 'hausa' | 'igbo' | 'yoruba'>('english');
  const [speakerName, setSpeakerName] = useState('idera');
  const [isLoading, setIsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  
  // Speech-to-Text states
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [sttLanguage, setSttLanguage] = useState('en-NG'); // Nigerian English
  const recognitionRef = useRef<any>(null);

  // Speech-to-Text language options (Web Speech API compatible codes)
  const sttLanguageOptions = [
    { value: 'en-NG', label: 'Nigerian English', code: 'en-NG' },
    { value: 'ha', label: 'Hausa', code: 'ha' },
    { value: 'ig', label: 'Igbo', code: 'ig' },
    { value: 'yo', label: 'Yoruba', code: 'yo' },
    { value: 'pcm', label: 'Nigerian Pidgin', code: 'en-NG' }, // Falls back to Nigerian English
  ];

  // Voice options by language
  const voiceOptions = {
    english: [
      { value: 'idera', label: 'Chimobi (Female)' },
      { value: 'tope', label: 'Tope (Female)' },
      { value: 'larai', label: 'Larai (Female)' },
      { value: 'uche', label: 'Uche (Male)' },
      { value: 'ade', label: 'Ade (Male)' },
    ],
    hausa: [
      { value: 'hausa_female1', label: 'Hausa Female 1' },
      { value: 'hausa_male1', label: 'Hausa Male 1' },
    ],
    igbo: [
      { value: 'igbo_female1', label: 'Igbo Female 1' },
      { value: 'igbo_male1', label: 'Igbo Male 1' },
    ],
    yoruba: [
      { value: 'yoruba_female1', label: 'Yoruba Female 1' },
      { value: 'yoruba_male1', label: 'Yoruba Male 1' },
    ],
  };

  // Initialize Speech Recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = sttLanguage;

        recognitionRef.current.onresult = (event: any) => {
          let interimTranscript = '';
          let finalTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }

          setTranscript((prev) => prev + finalTranscript);
        };

        recognitionRef.current.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
          if (event.error === 'no-speech') {
            toast.error('No speech detected. Please try again.');
          } else {
            toast.error('Speech recognition error. Please try again.');
          }
        };

        recognitionRef.current.onend = () => {
          setIsListening(false);
        };
      }
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [sttLanguage]);

  const handleTest = async () => {
    if (!text.trim()) {
      toast.error('Please enter some text to convert');
      return;
    }

    if (text.length > 5000) {
      toast.error('Text must be less than 5000 characters');
      return;
    }

    setIsLoading(true);
    setAudioUrl(null);

    try {
      const response = await apiClient.generateTTS({
        text: text.trim(),
        language,
        speaker_name: speakerName,
        temperature: 0.1,
        repetition_penalty: 1.1,
      });

      // Resolve audio URL - handle both relative and absolute URLs
      const audioUrl = response.audio_url.startsWith('http')
        ? response.audio_url
        : `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}${response.audio_url}`;
      
      setAudioUrl(audioUrl);
      toast.success('Audio generated successfully!');
    } catch (error: any) {
      console.error('TTS Error:', error);
      
      // Extract error info from various possible formats
      // Try multiple ways to get the response data
      const responseData = error.response?.data || error.response || {};
      const statusCode = error.statusCode || error.response?.status || responseData.status;
      const errorDetail = responseData.detail || error.message || 'Failed to generate audio';
      const limitType = responseData.limit_type;
      
      // Log for debugging
      console.log('Error details:', { statusCode, errorDetail, limitType, responseData });
      
      // Handle rate limit errors (429) with helpful message
      if (statusCode === 429 || limitType === 'anonymous' || 
          errorDetail.toLowerCase().includes('demo limit') || 
          errorDetail.toLowerCase().includes('rate limit') ||
          errorDetail.toLowerCase().includes('limit reached')) {
        toast.error(
          '🚀 Demo limit reached! Sign up for FREE to get 10,000 characters/month',
          { 
            duration: 6000,
            id: 'rate-limit-error' // Prevent duplicate toasts
          }
        );
        // Show additional info toast
        setTimeout(() => {
          toast('💡 Create a free account to continue generating audio', {
            icon: '👉',
            duration: 5000,
            id: 'rate-limit-info'
          });
        }, 1000);
      } else if (statusCode === 400 && errorDetail.toLowerCase().includes('character')) {
        // Character limit exceeded for demo
        toast.error(
          '📝 Demo mode: Max 200 characters. Sign up for FREE to get 10,000 characters/month!',
          { duration: 5000 }
        );
      } else if (errorDetail.includes('Network Error') || errorDetail.includes('timeout')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        // Always show error message in toast
        toast.error(errorDetail || 'Failed to generate audio. Please try again.', {
          duration: 5000
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartListening = () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition is not supported in your browser.');
      return;
    }

    setTranscript('');
    recognitionRef.current.lang = sttLanguage;
    setIsListening(true);
    recognitionRef.current.start();
    toast.success('Listening... Speak now!');
  };

  const handleStopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      if (transcript.trim()) {
        setText(transcript.trim());
        toast.success('Speech captured!');
      }
    }
  };

  // Update speaker when language changes
  const handleLanguageChange = (newLanguage: 'english' | 'hausa' | 'igbo' | 'yoruba') => {
    setLanguage(newLanguage);
    const voices = voiceOptions[newLanguage];
    if (voices.length > 0) {
      setSpeakerName(voices[0].value);
    }
  };

  return (
    <div className="tts-test-section border-30 bg-white mt-30 lg-mt-20 pt-120 lg-pt-80 pb-120 lg-pb-80">
      <div className="container">
        <div className="row align-items-stretch">
          <div className="col-lg-6 order-lg-first order-last d-flex flex-column">
            <div className="title-one mb-40">
              <h2>Try It Free</h2>
              <p className="fs-20 text-dark mt-20">
                Test our Nigerian-accented TTS API before you commit. No credit card required.
              </p>
            </div>
            <div className="test-form flex-grow-1 d-flex flex-column">
              {/* Speech-to-Text Section */}
              <div className="mb-30">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <label className="text-dark fw-500 mb-0" style={{ fontSize: '14px' }}>Speech to Text</label>
                  <select
                    value={sttLanguage}
                    onChange={(e) => setSttLanguage(e.target.value)}
                    disabled={isListening}
                    className="form-control"
                    style={{
                      width: 'auto',
                      minWidth: '160px',
                      borderRadius: '6px',
                      border: '1px solid #d0d0d0',
                      padding: '6px 32px 6px 10px',
                      fontSize: '13px',
                      height: '36px',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      appearance: 'none',
                      backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23333\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 10px center',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isListening) {
                        e.currentTarget.style.borderColor = '#007bff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d0d0d0';
                    }}
                  >
                    {sttLanguageOptions.map((option) => (
                      <option key={option.value} value={option.code}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="d-flex gap-2 mb-2">
                  {!isListening ? (
                    <button
                      className="btn-one flex-fill"
                      onClick={handleStartListening}
                      type="button"
                      style={{ height: '48px', fontSize: '16px', fontWeight: '600' }}
                    >
                      <i className="bi bi-mic me-2"></i>Start Recording
                    </button>
                  ) : (
                    <button
                      className="btn-two flex-fill"
                      onClick={handleStopListening}
                      type="button"
                      style={{ height: '48px', fontSize: '16px', fontWeight: '600' }}
                    >
                      <i className="bi bi-mic-fill me-2"></i>Stop Recording
                    </button>
                  )}
                </div>
                {transcript && (
                  <div className="p-3 bg-light border-30 rounded mb-2" style={{ minHeight: '40px' }}>
                    <small className="text-dark">{transcript}</small>
                  </div>
                )}
                <small className="text-muted" style={{ fontSize: '12px' }}>Select language and click to record</small>
              </div>

              {/* Text Input */}
              <div className="form-group mb-20 flex-grow-1 d-flex flex-column">
                <label className="d-block mb-2 text-dark fw-500">Enter Text</label>
                <textarea
                  className="form-control flex-grow-1"
                  rows={6}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type or paste your text here..."
                  style={{ 
                    resize: 'vertical',
                    borderRadius: '8px',
                    border: '2px solid #e0e0e0',
                    padding: '12px',
                    fontSize: '16px'
                  }}
                />
                <small className="text-dark mt-1 d-block">
                  {text.length} / 5000 characters
                </small>
              </div>

              {/* Language and Voice Selection */}
              <div className="row mb-20">
                <div className="col-md-6 mb-3 mb-md-0">
                  <label className="d-block mb-2 text-dark fw-500">Language</label>
                  <select
                    className="form-control"
                    value={language}
                    onChange={(e) => handleLanguageChange(e.target.value as any)}
                    style={{
                      borderRadius: '8px',
                      border: '2px solid #e0e0e0',
                      padding: '12px',
                      fontSize: '16px',
                      height: '48px'
                    }}
                  >
                    <option value="english">English (Nigerian Accent)</option>
                    <option value="hausa">Hausa</option>
                    <option value="igbo">Igbo</option>
                    <option value="yoruba">Yoruba</option>
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="d-block mb-2 text-dark fw-500">Voice</label>
                  <select
                    className="form-control"
                    value={speakerName}
                    onChange={(e) => setSpeakerName(e.target.value)}
                    style={{
                      borderRadius: '8px',
                      border: '2px solid #e0e0e0',
                      padding: '12px',
                      fontSize: '16px',
                      height: '48px'
                    }}
                  >
                    {voiceOptions[language].map((voice) => (
                      <option key={voice.value} value={voice.value}>
                        {voice.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Generate Button */}
              <button
                className="btn-one w-100"
                onClick={handleTest}
                disabled={isLoading || !text.trim()}
                style={{ height: '48px', fontSize: '16px', fontWeight: '600' }}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Generating Audio...
                  </>
                ) : (
                  'Generate Speech'
                )}
              </button>

              {/* Audio Player */}
              {audioUrl && (
                <div className="mt-30 p-4 bg-light border-30 rounded">
                  <h5 className="mb-3 fw-500">Generated Audio</h5>
                  <audio controls className="w-100 mb-3" src={audioUrl}>
                    Your browser does not support the audio element.
                  </audio>
                  <div>
                    <a href={audioUrl} download className="btn-two">
                      <i className="bi bi-download me-2"></i>Download Audio
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="col-lg-6 order-lg-last order-first mb-40 mb-lg-0">
            <div className="img-holder text-center position-relative h-100">
              <img
                src="/images/assets/test-section-hero.jpeg"
                alt="Try ameboGPT TTS API"
                className="w-100 h-100"
                style={{
                  maxWidth: '100%',
                  width: '100%',
                  height: '100%',
                  minHeight: '600px',
                  borderRadius: '20px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://undraw.co/illustrations/voice-interface.svg';
                }}
              />
              {/* Decorative unDraw illustration overlay for depth */}
              <img
                src="https://undraw.co/illustrations/sound-wave.svg"
                alt=""
                className="position-absolute d-none d-lg-block"
                style={{
                  width: '120px',
                  height: '120px',
                  bottom: '-30px',
                  right: '-30px',
                  opacity: 0.15,
                  zIndex: -1
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TTSTestSection;
